import React from 'react';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';

import GenericDataProvider from '../../common/api/GenericDataProvider';
import { AdminWidget, DataTable } from '../common';
import { useDialog } from '../../common/context/DialogContext';
import { getObjectActions } from '../common/ObjectActionsConfig';
import useMutateData from '../hooks/useMutateData';
import {
  columnConfig, searchConfig, AdminObjectName, ColumnDefinition,
} from '../common/DataColumnConfig';
import useAdminModuleApprovalHook from '../hooks/useAdminModuleApproval';
import log from '../../common/utils/logger';

import {
  DataTableActionConfig,
} from '../common/DataTable/types';

interface GenericPanelProps {
  route: {
    params: {
      title?: string;
      objectName: AdminObjectName;
      columns?: Record<string, ColumnDefinition<unknown>>;
      filter?: Record<string, unknown>;
      actions?: Record<string, unknown>;
      showSearch?: boolean;
    };
  };
}

/**
 * A generic panel component that renders a DataTable for a specific object type.
 * Handles common actions like create, update, delete, and filtering.
 */
export const GenericPanel: React.FC<GenericPanelProps> = ({ route }) => {
  const {
    title,
    objectName,
    columns: paramsColumns,
    filter: paramsFilter,
    actions: routeActions,
    showSearch,
  } = route.params;

  const navigation = useNavigation();
  const {
    showDialog, hideDialog, showToastMessage, updateDialogProps,
  } = useDialog();
  const queryClient = useQueryClient();

  const [refreshKey, setRefreshKey] = React.useState(Date.now());
  const [isActionRunning, setIsActionRunning] = React.useState(false);

  const {
    createMutation, updateMutation, deleteMutation, customQueryMutation,
  } = useMutateData(objectName);

  // Specialized hooks for certain object types
  const moduleApprovalHooks = useAdminModuleApprovalHook();

  const columns = React.useMemo(() => {
    if (paramsColumns && typeof paramsColumns !== 'string') return paramsColumns;

    const typedColumnConfig = columnConfig as Record<string, Record<string, ColumnDefinition<unknown>>>;
    if (typedColumnConfig[objectName]) return typedColumnConfig[objectName];

    if (objectName?.includes('/')) {
      const parts = objectName.split('/');
      return parts.reduce(
        (acc: Record<string, unknown> | undefined, part) => (acc?.[part] as Record<string, unknown> | undefined),
        typedColumnConfig as unknown as Record<string, unknown>,
      ) as Record<string, ColumnDefinition<unknown>> || {};
    }
    return {};
  }, [paramsColumns, objectName]);

  const filter = React.useMemo(() => {
    if (paramsFilter && typeof paramsFilter !== 'string') return paramsFilter;
    return paramsFilter || {};
  }, [paramsFilter]);

  const objectActions = React.useMemo(() => {
    const hookFunctions = {
      'modules/approvals': objectName === 'modules/approvals' ? moduleApprovalHooks : null,
    };
    return getObjectActions(objectName, hookFunctions);
  }, [objectName, moduleApprovalHooks]);

  const allActions = React.useMemo(() => {
    if (!routeActions) return objectActions;
    return {
      ...objectActions,
      ...routeActions,
    };
  }, [routeActions, objectActions]);

  const typedSearchConfig = searchConfig as Record<string, { defaultSearchField: string; searchableFields?: string[] }>;
  const objectSearchConfig = typedSearchConfig[objectName] || { defaultSearchField: 'title' };

  const handleTableAction = async (
    actionCode: string,
    rowData: unknown,
    callbacks?: { onClearSelection?: () => void; onExitSelectionMode?: () => void },
  ) => {
    if (isActionRunning) return;

    const actionConfig = (allActions as Required<DataTableActionConfig>['actions'])[actionCode];

    if (!actionConfig?.handler) {
      log.warn(`No handler found for action: ${actionCode}`);
      return;
    }

    try {
      setIsActionRunning(true);
      await actionConfig.handler({
        data: rowData,
        objectName,
        mutations: {
          createMutation,
          updateMutation,
          deleteMutation,
          customQueryMutation,
        },
        utils: {
          showDialog,
          hideDialog,
          updateDialogProps,
          showToastMessage,
          setRefreshKey,
          navigation,
          dataProvider: new GenericDataProvider(),
          setActionRunning: setIsActionRunning,
          queryClient,
          onClearSelection: callbacks?.onClearSelection,
          onExitSelectionMode: callbacks?.onExitSelectionMode,
        },
      });
    } catch (error: unknown) {
      log.error(`Error executing action ${actionCode}:`, error);
      showToastMessage('error', `Failed to execute ${actionCode}: ${(error as Error).message}`);
    } finally {
      setIsActionRunning(false);
    }
  };

  if (!objectName) {
    log.error('GenericPanel: objectName not provided');
    return null;
  }

  return (
    <AdminWidget title="" w="100%" h="100%">
      <DataTable
        dataConfig={{
          objectName,
          refreshKey,
        }}
        displayConfig={{
          tableTitle: title,
          columns,
        }}
        actionConfig={{
          actions: allActions as Required<DataTableActionConfig>['actions'],
          onAction: handleTableAction,
          isActionRunning,
        }}
        searchFilterConfig={{
          showSearch: showSearch ?? true,
          defaultSearchField: objectSearchConfig.defaultSearchField,
          searchableFields: objectSearchConfig.searchableFields,
          filter,
        }}
        paginationSelectionConfig={{
          enableSelection: true,
        }}
      />
    </AdminWidget>
  );
};

export default GenericPanel;
